import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Info, Loader2 } from "lucide-react";
import type { SpreadsheetComponent } from "@syncfusion/ej2-react-spreadsheet";
import { useAccessToken } from "@/auth/useAccessToken";
import { driveApi, explainSaveError, isDriveUrl, type DriveItem } from "@/api/driveApi";
import { useSaveTracker } from "@/lib/useSaveTracker";
import { CloseFileButton, FileTitle } from "@/features/shell/FileTitle";
import { ShellHeaderPortal, useShellLeaveGuard } from "@/features/shell/ShellHeader";
import type { EditorLaunchState } from "@/features/home/homeModel";
import { DriveOpenDialog } from "./DriveOpenDialog";
import { buildSheetTemplate } from "./sheetTemplates";
import { driveFileApi } from "@/features/drive-file/driveFileApi";
import { DriveLocation } from "@/features/drive-file/DriveLocation";
import { ShareButton } from "@/features/drive-file/ShareDialog";
import {
    HistoryButton, VersionHistoryPanel, VIEWER_HISTORY_MESSAGE, isHistoryShortcut
} from "@/features/drive-file/VersionHistoryPanel";
import {
    CommentsButton, CommentsPanel, isCommentsShortcut, useOpenCommentCount
} from "@/features/drive-file/CommentsPanel";
import { sheetCommentsAdapter } from "./sheetComments";
import { FreezeMenu, SheetStatusBar, useSelectionAggregate } from "./SheetTools";

// Spreadsheet is ~3 MB minified; lazy-load so the shell mounts quickly.
const SpreadsheetHost = lazy(() =>
    // memo: the page re-renders on every selection (status bar summary); the
    // host's props are stable, so the Spreadsheet wrapper isn't re-diffed.
    import("./SpreadsheetHost").then((m) => ({ default: memo(m.SpreadsheetHost) }))
);

// Self-hosted Syncfusion Excel service (Athavita.Sheet.Bff): /Open turns
// .xlsx / .xls / .csv into workbook JSON, /SaveAs renders JSON to bytes.
const SERVICE_BASE = (import.meta.env.VITE_SHEET_SERVICE_URL as string | undefined)?.replace(/\/$/, "");

const OPENABLE_EXT_RE = /\.(xlsx?|csv|json)$/i;
const KNOWN_EXT_RE = /\.(xlsx?|csv|json|pdf)$/i;
// Edits the component reports right after a load belong to the load.
const LOAD_SETTLE_MS = 250;
const OPEN_TIMEOUT_MS = 120_000;

type SheetFormat = "xlsx" | "xls" | "csv" | "pdf";
type SaveMode = "manual" | "auto";

const SAVE_TYPE: Record<SheetFormat, "Xlsx" | "Xls" | "Csv" | "Pdf"> = {
    xlsx: "Xlsx", xls: "Xls", csv: "Csv", pdf: "Pdf"
};

/** The Drive file the editor is bound to — Save writes a new version of it. */
interface TrackedFile {
    id: string;
    name: string;
    parentId: string | null;
    /** "viewer" / "editor" when someone else shared the file; unset for your own. */
    role?: string | null;
}

export function SheetPage() {
    const [params] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const token = useAccessToken();
    // Autosave fires from a timer — read the freshest token / navigate.
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    // Launch parameters are read once: the URL is rewritten as files are
    // opened and saved, and that must never re-trigger a load.
    const launchRef = useRef({
        fileId: params.get("fileId"),
        openFile: params.get("openFile"),
        fileName: params.get("fileName"),
        // From the home screen: a picked local file / a template id.
        localFile: (location.state as EditorLaunchState | null)?.localFile ?? null,
        template: (location.state as EditorLaunchState | null)?.template ?? null
    });

    const editorRef = useRef<SpreadsheetComponent>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const trackedRef = useRef<TrackedFile | null>(null);
    const [tracked, setTrackedState] = useState<TrackedFile | null>(null);
    const [fileName, setFileName] = useState(launchRef.current.fileName ?? "Untitled.xlsx");
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);
    const settleTimerRef = useRef<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const saveNowRef = useRef<(mode: SaveMode) => Promise<void>>(async () => {});
    const { status, markDirty, markClean, setSuppressed, runSave, hasUnsavedChanges, whenSaved } = useSaveTracker({
        onAutosave: () => void saveNowRef.current("auto"),
        onError: setError
    });

    // Binds the editor to a Drive file (null = unbound) and mirrors the
    // binding into the URL — `?fileId=` only — so a reload reopens it.
    const setTracked = useCallback((file: TrackedFile | null) => {
        const previousId = trackedRef.current?.id ?? null;
        trackedRef.current = file;
        setTrackedState(file);
        if (file) setFileName(file.name);
        if ((file?.id ?? null) !== previousId) {
            navigateRef.current(
                { search: file ? `?fileId=${encodeURIComponent(file.id)}` : "" },
                { replace: true }
            );
        }
    }, []);

    const confirmDiscard = useCallback(
        () => !hasUnsavedChanges() || window.confirm("You have unsaved changes that will be lost. Continue?"),
        [hasUnsavedChanges]
    );
    useShellLeaveGuard(confirmDiscard);

    // Close (top bar X / File → Close) → Sheets home. A save in flight
    // finishes first — never cut off; whatever is still unsaved after it asks
    // first. Replace: Back from home doesn't reopen the file just closed.
    const [closing, setClosing] = useState(false);
    const closingRef = useRef(false);
    const closeFile = useCallback(async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        setClosing(true);
        try {
            await whenSaved();
            if (hasUnsavedChanges() && !window.confirm("You have unsaved changes. Close without saving?")) return;
            markClean();
            navigateRef.current("/", { replace: true });
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    }, [hasUnsavedChanges, markClean, whenSaved]);

    // ss.open() is fire-and-forget: the workbook arrives after a round-trip
    // to the service, signalled by openComplete / openFailure.
    const pendingOpenRef = useRef<{ resolve: () => void; reject: (err: Error) => void } | null>(null);
    const openWorkbookFile = useCallback((ss: SpreadsheetComponent, file: File) => new Promise<void>((resolve, reject) => {
        pendingOpenRef.current?.reject(new Error("Superseded by another open."));
        let timer = 0;
        const settle = () => {
            window.clearTimeout(timer);
            pendingOpenRef.current = null;
        };
        pendingOpenRef.current = {
            resolve: () => { settle(); resolve(); },
            reject: (err) => { settle(); reject(err); }
        };
        timer = window.setTimeout(
            () => pendingOpenRef.current?.reject(new Error(`Timed out opening ${file.name}.`)),
            OPEN_TIMEOUT_MS
        );
        try {
            ss.open({ file });
        } catch (err) {
            pendingOpenRef.current?.reject(err instanceof Error ? err : new Error(String(err)));
        }
    }), []);
    const onOpenComplete = useCallback(() => pendingOpenRef.current?.resolve(), []);
    const onOpenFailure = useCallback(
        () => pendingOpenRef.current?.reject(new Error("The spreadsheet service couldn't open this file.")),
        []
    );

    // Loads file bytes: .json snapshots directly, Excel / CSV through the
    // service — the same path for Drive files, `?fileId=` and local picks.
    const loadBlob = useCallback(async (ss: SpreadsheetComponent, blob: Blob, name: string) => {
        if (/\.json$/i.test(name)) {
            ss.openFromJson({ file: JSON.parse(await blob.text()) });
            return;
        }
        if (!SERVICE_BASE) throw new Error("VITE_SHEET_SERVICE_URL not set — can't open .xlsx / .xls / .csv");
        await openWorkbookFile(ss, new File([blob], name, { type: blob.type || "application/octet-stream" }));
    }, [openWorkbookFile]);

    // Wraps every workbook load. Change events the load itself causes are
    // ignored; a failed load leaves the previous workbook's state alone.
    const runLoad = useCallback(async (load: (ss: SpreadsheetComponent) => Promise<void>): Promise<boolean> => {
        const ss = editorRef.current;
        if (!ss) return false;
        if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        setSuppressed(true);
        try {
            await load(ss);
            markClean();
            return true;
        } catch (err) {
            setError(errorMessage(err));
            return false;
        } finally {
            loadingRef.current = false;
            setLoading(false);
            settleTimerRef.current = window.setTimeout(() => {
                settleTimerRef.current = null;
                setSuppressed(false);
            }, LOAD_SETTLE_MS);
        }
    }, [markClean, setSuppressed]);

    const openDriveFile = useCallback((file: TrackedFile) => runLoad(async (ss) => {
        if (!OPENABLE_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
        const blob = await driveApi.downloadBlob(file.id, tokenRef.current);
        await loadBlob(ss, blob, file.name);
        setTracked(file);
    }), [loadBlob, runLoad, setTracked]);

    const onLocalPick: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file || !confirmDiscard()) return;
        void runLoad(async (ss) => {
            if (!OPENABLE_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
            await loadBlob(ss, file, file.name);
            setTracked(null);
            setFileName(file.name);
        });
    };

    // Final menu: New, Open, Open from Drive, Save, Save As…, Version
    // history, Print, Close
    // `hideFileMenuItems(["Save As"], true)` is called at creation but
    // Syncfusion v33 re-adds the native "Save As" (with format submenu)
    // when the menu popup opens — so we also DOM-scrub it in
    // `fileMenuBeforeOpen` below as a belt-and-braces fix.
    const addedRef = useRef(false);
    const primeFileMenu = useCallback(() => {
        if (addedRef.current) return;
        const ss = editorRef.current as unknown as {
            addFileMenuItems?: (items: Array<Record<string, unknown>>, text: string, insertAfter: boolean) => void;
            hideFileMenuItems?: (texts: string[], hide?: boolean) => void;
            hideToolbarItems?: (tab: string, indexes: number[], hide?: boolean) => void;
        };
        if (typeof ss?.addFileMenuItems !== "function") return;
        try {
            ss.hideFileMenuItems?.(["Save As"], true);
            // Review tab: hide Syncfusion's Comment dropdown (+ its separator).
            // Its comments live only inside the workbook, apart from the
            // file's Drive comments (header button / context menu → Comment).
            // Cell Notes stay — they're ordinary Excel notes.
            ss.hideToolbarItems?.("Review", [0, 1], true);
            ss.addFileMenuItems(
                [{ id: "athavita-open-drive", text: "Open from Drive", iconCss: "e-icons e-folder-open" }],
                "Open", true
            );
            ss.addFileMenuItems(
                [{ id: "athavita-save", text: "Save", iconCss: "e-icons e-save" }],
                "Open from Drive", true
            );
            ss.addFileMenuItems(
                [{ id: "athavita-save-as", text: "Save As…", iconCss: "e-icons e-save" }],
                "Save", true
            );
            ss.addFileMenuItems(
                [{ id: "athavita-history", text: "Version history", iconCss: "e-icons e-history" }],
                "Save As…", true
            );
            // Last, after the native Print: close the file → Sheets home.
            ss.addFileMenuItems(
                [{ id: "athavita-close", text: "Close", iconCss: "e-icons e-close" }],
                "Print", true
            );
            addedRef.current = true;
        } catch (err) {
            console.error("[SheetPage] primeFileMenu failed", err);
        }
    }, []);

    // Belt-and-braces: `hideFileMenuItems` doesn't always suppress the
    // native "Save As" (with its Xlsx/Xls/Csv/Pdf submenu) — strip it
    // from the DOM every time the file popup opens. We keep our own
    // "Save As…" (ellipsis) entry; anything else matching the plain
    // "Save As" text gets removed.
    const fileMenuBeforeOpen = useCallback((args: { element?: HTMLElement }) => {
        const root = args.element;
        if (!root) return;
        const scrub = () => {
            const items = Array.from(root.querySelectorAll("li.e-menu-item")) as HTMLLIElement[];
            for (const li of items) {
                if (li.id?.startsWith("athavita-")) continue;
                const text = (li.textContent ?? "").trim();
                if (text === "Save As") li.remove();
            }
        };
        queueMicrotask(scrub);
        setTimeout(scrub, 0);
    }, []);

    const loadInitial = useCallback(() => {
        if (!editorRef.current) return;
        primeFileMenu();
        const { fileId, openFile, fileName: launchName, localFile, template } = launchRef.current;
        if (template) {
            // A home-screen template: a new, unsaved workbook. Drop the router
            // state so a reload opens blank rather than re-applying it.
            navigateRef.current({ search: "" }, { replace: true });
            const built = buildSheetTemplate(template);
            void runLoad(async (ss) => {
                if (built) ss.openFromJson({ file: built.workbook });
                setTracked(null);
                setFileName(built?.fileName ?? "Untitled.xlsx");
            });
            return;
        }
        if (localFile) {
            // Picked on the home screen (the picker needs the click's user
            // activation, which is gone by the time this chunk has loaded).
            navigateRef.current({ search: "" }, { replace: true });
            void runLoad(async (ss) => {
                if (!OPENABLE_EXT_RE.test(localFile.name)) throw new Error(unsupportedMessage(localFile.name));
                await loadBlob(ss, localFile, localFile.name);
                setTracked(null);
                setFileName(localFile.name);
            });
            return;
        }
        if (fileId) {
            // Keep `fileId` (a reload reopens the file); drop Drive's
            // one-shot `openFile` / `fileName` hints.
            navigateRef.current({ search: `?fileId=${encodeURIComponent(fileId)}` }, { replace: true });
            void runLoad(async (ss) => {
                const meta = await driveApi.getItem(fileId, tokenRef.current);
                setFileName(meta.name);
                if (!OPENABLE_EXT_RE.test(meta.name)) throw new Error(unsupportedMessage(meta.name));
                const blob = await driveApi.downloadBlob(meta.id, tokenRef.current);
                await loadBlob(ss, blob, meta.name);
                setTracked(toTracked(meta));
            });
        } else if (openFile) {
            navigateRef.current({ search: "" }, { replace: true });
            const name = launchName ?? "workbook.json";
            void runLoad(async (ss) => {
                if (!OPENABLE_EXT_RE.test(name)) throw new Error(unsupportedMessage(name));
                // Only Drive gets the bearer token — `openFile` comes from the URL.
                const headers = new Headers();
                if (tokenRef.current && isDriveUrl(openFile)) headers.set("Authorization", `Bearer ${tokenRef.current}`);
                const res = await fetch(openFile, { headers, credentials: "omit" });
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                await loadBlob(ss, await res.blob(), name);
                setFileName(name);
            });
        }
    }, [loadBlob, primeFileMenu, runLoad, setTracked]);

    // Save As → Drive, or a Save that can't version the open file: creates
    // a new file in the open file's folder, then binds the editor to it so
    // the next Save versions the copy rather than the original.
    const saveAsNewDriveFile = useCallback(async (baseName: string, type: Exclude<SheetFormat, "pdf">) => {
        const ss = editorRef.current;
        if (!ss) return;
        await runSave(async () => {
            const blob = await renderWorkbook(ss, baseName, type, tokenRef.current);
            const saved = await driveApi.uploadBlob(
                blob, `${baseName}.${type}`, trackedRef.current?.parentId ?? null, tokenRef.current
            );
            setTracked(toTracked(saved));
        });
    }, [runSave, setTracked]);

    const saveNow = useCallback(async (mode: SaveMode) => {
        const ss = editorRef.current;
        if (!ss || loadingRef.current) return;
        const current = trackedRef.current;
        if (!current) {
            // Never saved — only an explicit Save asks where to put it.
            if (mode === "manual") setSaveAsOpen(true);
            return;
        }
        const format = versionFormat(current.name);
        if (!format) {
            // A .json snapshot can't take Excel bytes as a new version, so an
            // explicit Save writes an .xlsx copy next to it instead.
            if (mode === "manual") await saveAsNewDriveFile(stripExtension(current.name), "xlsx");
            return;
        }
        // A view-only share can't take a new version — don't autosave into a
        // guaranteed failure; an explicit Save explains why.
        if (mode === "auto" && current.role === "viewer") return;
        if (mode === "manual") setError(null);
        await runSave(async () => {
            const blob = await renderWorkbook(ss, stripExtension(current.name), format, tokenRef.current);
            const saved = await driveApi
                .uploadVersion(current.id, blob, current.name, tokenRef.current)
                .catch((err: unknown) => { throw explainSaveError(err, current.role); });
            if (trackedRef.current?.id === saved.id) setTracked(toTracked(saved));
        });
    }, [runSave, saveAsNewDriveFile, setTracked]);
    saveNowRef.current = saveNow;

    // Version history — owners and editors of a Drive file only.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [commentsOpen, setCommentsOpen] = useState(false);
    const openHistory = useCallback(() => {
        const current = trackedRef.current;
        if (!current) setError("Save this workbook to Drive to keep a version history.");
        else if (current.role === "viewer") setError(VIEWER_HISTORY_MESSAGE);
        else {
            setCommentsOpen(false);
            setHistoryOpen(true);
        }
    }, []);
    useEffect(() => {
        if (!tracked || tracked.role === "viewer") setHistoryOpen(false);
        if (!tracked) setCommentsOpen(false);
    }, [tracked]);

    // Comments — anyone who can open the Drive file, viewers included.
    const commentCount = useOpenCommentCount(tracked?.id ?? null);
    const commentsAdapter = useMemo(() => sheetCommentsAdapter(() => editorRef.current), []);
    const openComments = useCallback(() => {
        if (!trackedRef.current) {
            setError("Save this workbook to Drive to comment on it.");
            return;
        }
        setHistoryOpen(false);
        setCommentsOpen(true);
    }, []);

    // Drive adds a new version holding the old bytes; reload it into the
    // editor and stay bound to the file.
    const restoreVersion = useCallback(async (versionId: string, discard: boolean) => {
        const current = trackedRef.current;
        if (!current) throw new Error("This workbook isn't in Drive.");
        if (!discard && hasUnsavedChanges()) throw new Error("Save or discard your changes before restoring.");
        // Dropping the edits first stops autosave writing them over the restore.
        if (discard) markClean();
        let restored: DriveItem;
        try {
            restored = await driveFileApi.restoreVersion(current.id, versionId, tokenRef.current);
        } catch (err) {
            if (discard) markDirty();
            throw err;
        }
        const ok = await openDriveFile({ ...toTracked(restored), role: restored.role ?? current.role ?? null });
        if (!ok) throw new Error("The version was restored in Drive, but the editor couldn't reload it — reopen the file to see it.");
    }, [hasUnsavedChanges, markClean, markDirty, openDriveFile]);

    // Ctrl+S → Save; Ctrl+Shift+S → Save As; Ctrl+Alt+Shift+H → Version history
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isHistoryShortcut(e)) {
                e.preventDefault();
                openHistory();
                return;
            }
            if (isCommentsShortcut(e)) {
                e.preventDefault();
                openComments();
                return;
            }
            if (e.key.toLowerCase() === "s" && (e.ctrlKey || e.metaKey) && !e.altKey) {
                e.preventDefault();
                if (e.shiftKey) setSaveAsOpen(true);
                else void saveNowRef.current("manual");
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [openHistory, openComments]);

    // Inline title rename. Bound to Drive → rename the file there; otherwise
    // it's just the name the next Save As suggests.
    const renameFile = useCallback(async (typed: string) => {
        const current = trackedRef.current;
        const next = keepExtension(typed, current?.name ?? fileName);
        if (!current) {
            setFileName(next);
            return;
        }
        const updated = await driveApi.renameItem(current.id, next, tokenRef.current);
        setTracked(toTracked(updated));
    }, [fileName, setTracked]);

    // Drive file picker modal state — triggered by "Open from Drive"
    // inside Syncfusion's File menu.
    // (The home screen's "Open file → From Drive" uses the same dialog.)
    const [driveDialogOpen, setDriveDialogOpen] = useState(false);
    const openFromDrive = useCallback(() => setDriveDialogOpen(true), []);

    const pickDriveFile = useCallback((item: DriveItem) => {
        setDriveDialogOpen(false);
        if (!confirmDiscard()) return;
        void openDriveFile(toTracked(item));
    }, [confirmDiscard, openDriveFile]);

    // Download the current workbook as .xlsx via Syncfusion's save
    // pipeline. Triggers a browser download, no Drive upload.
    const handleDownload = useCallback(() => {
        const ss = editorRef.current;
        if (!ss) return;
        setError(null);
        void downloadWorkbook(ss, stripExtension(fileName), "xlsx", tokenRef.current)
            .catch((err: unknown) => setError(errorMessage(err)));
    }, [fileName]);

    // Native "New" asks its own "discard workbook?" question and then calls
    // refresh(true); we do the same so the Drive binding is dropped too.
    const newWorkbook = useCallback(() => {
        if (!confirmDiscard()) return;
        void runLoad(async (ss) => {
            ss.refresh(true);
            setTracked(null);
            setFileName("Untitled.xlsx");
        });
    }, [confirmDiscard, runLoad, setTracked]);

    const [saveAsOpen, setSaveAsOpen] = useState(false);

    // Handle clicks on our custom file-menu items, and take over the native
    // New / Open (matched by text — their ids are `${spreadsheetId}_New`…)
    // so the Drive binding and unsaved-changes state stay correct. Native
    // "Save As" is hidden via primeFileMenu, but as a safety net we also
    // intercept its text here in case a future Syncfusion release changes
    // how hideFileMenuItems behaves.
    const fileMenuItemSelect = useCallback((args: { item?: { id?: string; text?: string }; cancel?: boolean }) => {
        const id = args.item?.id ?? "";
        const text = args.item?.text ?? "";
        if (text === "New") { args.cancel = true; newWorkbook(); return; }
        if (text === "Open") { args.cancel = true; fileInputRef.current?.click(); return; }
        if (text === "Save As") { args.cancel = true; setSaveAsOpen(true); return; }
        if (!id.startsWith("athavita-")) return;
        args.cancel = true;
        if (id === "athavita-save") void saveNowRef.current("manual");
        else if (id === "athavita-save-as") setSaveAsOpen(true);
        else if (id === "athavita-open-drive") void openFromDrive();
        else if (id === "athavita-download") handleDownload();
        else if (id === "athavita-history") openHistory();
        else if (id === "athavita-close") void closeFile();
    }, [newWorkbook, openFromDrive, handleDownload, openHistory, closeFile]);

    // The spreadsheet's own File → Save As: saved through our fetch, which
    // carries the sign-in token (its form POST can't).
    const onNativeSave = useCallback((name: string, saveType: string) => {
        const ss = editorRef.current;
        if (!ss) return;
        const t = saveType.toLowerCase();
        const type: SheetFormat = t === "xls" || t === "csv" || t === "pdf" ? t : "xlsx";
        void downloadWorkbook(ss, stripExtension(name || "Untitled"), type, tokenRef.current)
            .catch((err: unknown) => setError(errorMessage(err)));
    }, []);

    // Save As confirm: upload to Drive or download locally, using the
    // chosen filename + filetype.
    const handleSaveAsConfirm = useCallback(async (target: "drive" | "computer", name: string, type: SheetFormat) => {
        setSaveAsOpen(false);
        const ss = editorRef.current;
        if (!ss) return;
        setError(null);
        const cleanName = stripExtension(name || "Untitled");

        if (target === "computer") {
            try {
                await downloadWorkbook(ss, cleanName, type, tokenRef.current);
            } catch (err) {
                setError(errorMessage(err));
            }
            return;
        }
        if (type === "pdf") {
            // A PDF export isn't the workbook: upload it next to the open
            // file but stay bound to (and dirty against) the workbook.
            try {
                const blob = await renderWorkbook(ss, cleanName, "pdf", tokenRef.current);
                await driveApi.uploadBlob(blob, `${cleanName}.pdf`, trackedRef.current?.parentId ?? null, tokenRef.current);
            } catch (err) {
                setError(errorMessage(err));
            }
            return;
        }
        await saveAsNewDriveFile(cleanName, type);
    }, [saveAsNewDriveFile]);

    // Selection summary (status bar) — refreshed on select, edits and sheet switches.
    const getSheet = useCallback(() => editorRef.current, []);
    const [aggregate, scheduleAggregate] = useSelectionAggregate(getSheet);
    const onSheetEdit = useCallback(() => {
        markDirty();
        scheduleAggregate();
    }, [markDirty, scheduleAggregate]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <ShellHeaderPortal>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <FileTitle
                        name={fileName}
                        status={status}
                        inDrive={tracked !== null}
                        loading={loading}
                        onRename={renameFile}
                        onRetry={() => void saveNowRef.current("manual")}
                        location={tracked ? (
                            <DriveLocation
                                fileId={tracked.id}
                                parentId={tracked.parentId}
                                shared={Boolean(tracked.role)}
                                tone="onPrimary"
                                className="hidden sm:flex"
                            />
                        ) : null}
                        accessory={
                            <>
                                {tracked ? (
                                    <HistoryButton
                                        tone="onPrimary"
                                        onClick={openHistory}
                                        disabledReason={tracked.role === "viewer" ? VIEWER_HISTORY_MESSAGE : null}
                                    />
                                ) : null}
                                <CommentsButton
                                    file={tracked}
                                    tone="onPrimary"
                                    openCount={commentCount.count}
                                    active={commentsOpen}
                                    onClick={commentsOpen ? () => setCommentsOpen(false) : openComments}
                                />
                            </>
                        }
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-2 pl-1">
                        <FreezeMenu getSheet={getSheet} onError={setError} />
                        <ShareButton file={tracked} tone="onPrimary" />
                        <CloseFileButton appName="Sheets" busy={closing} onClose={() => void closeFile()} />
                    </div>
                </div>
            </ShellHeaderPortal>
            {historyOpen && tracked ? (
                <VersionHistoryPanel
                    file={tracked}
                    onClose={() => setHistoryOpen(false)}
                    refreshKey={status}
                    saving={status === "saving"}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onRestore={restoreVersion}
                />
            ) : null}
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.json"
                hidden
                onChange={onLocalPick}
            />
            {error ? (
                <p className="flex shrink-0 items-start gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{error}</span>
                </p>
            ) : null}
            {/* Body row: the workbook, and Comments docked beside it on lg+
                (mock `.side.r`; an overlay below that). Version history stays
                an overlay; opening either closes the other. */}
            <div className="relative flex min-h-0 flex-1">
                <div className="relative min-h-0 min-w-0 flex-1 bg-[var(--sheets-canvas)]">
                    <Suspense fallback={
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading editor…
                        </div>
                    }>
                        <SpreadsheetHost
                            editorRef={editorRef}
                            onCreated={loadInitial}
                            fileMenuItemSelect={fileMenuItemSelect}
                            fileMenuBeforeOpen={fileMenuBeforeOpen}
                            onEdit={onSheetEdit}
                            onSelectionChange={scheduleAggregate}
                            onNewComment={openComments}
                            onOpenComplete={onOpenComplete}
                            onOpenFailure={onOpenFailure}
                            accessToken={token}
                            onNativeSave={onNativeSave}
                        />
                    </Suspense>
                </div>
                {commentsOpen && tracked ? (
                    <CommentsPanel
                        key={tracked.id}
                        file={tracked}
                        adapter={commentsAdapter}
                        docked
                        onClose={() => setCommentsOpen(false)}
                        onOpenCountChange={commentCount.set}
                    />
                ) : null}
            </div>
            <SheetStatusBar
                aggregate={aggregate}
                status={status}
                inDrive={tracked !== null}
                loading={loading}
                onRetry={() => void saveNowRef.current("manual")}
            />
            {driveDialogOpen ? (
                <DriveOpenDialog onClose={() => setDriveDialogOpen(false)} onPick={pickDriveFile} />
            ) : null}
            {saveAsOpen ? (
                <SaveAsDialog
                    defaultName={stripExtension(fileName)}
                    defaultType={inferSheetType(fileName)}
                    onClose={() => setSaveAsOpen(false)}
                    onConfirm={handleSaveAsConfirm}
                />
            ) : null}
        </div>
    );
}

/**
 * Renders the live workbook to file bytes through the service's /SaveAs
 * controller — the endpoint that actually streams bytes back. (The
 * minimal-API /Save returns an empty body, which is why Ctrl+S used to
 * upload a 0-byte file that Drive rejected.)
 */
async function renderWorkbook(ss: SpreadsheetComponent, baseName: string, type: SheetFormat, token: string | undefined): Promise<Blob> {
    if (!SERVICE_BASE) throw new Error("Save service is not configured (VITE_SHEET_SERVICE_URL).");
    // Syncfusion v33 types saveAsJson's result as `object`; at runtime it
    // is still `{ jsonObject }`.
    const result = await ss.saveAsJson();
    const jsonObj = (result as { jsonObject: unknown }).jsonObject;
    const form = new FormData();
    form.append("JSONData", JSON.stringify(jsonObj));
    form.append("fileName", baseName);
    form.append("saveType", SAVE_TYPE[type]);
    form.append("pdfLayoutSettings", JSON.stringify({ fitSheetOnOnePage: false }));
    const res = await fetch(`${SERVICE_BASE}/SaveAs`, {
        method: "POST",
        body: form,
        // The Sheet service is moving behind sign-in.
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: "omit"
    });
    if (!res.ok) throw new Error(`Converter ${res.status}: ${await res.text()}`);
    const blob = await res.blob();
    if (blob.size === 0) throw new Error("The save service returned an empty file — nothing was saved.");
    return blob;
}

/**
 * Save to the computer: render through /SaveAs (with the sign-in token) and
 * download. Replaces Syncfusion's save(), a plain form POST that can't carry
 * a token.
 */
async function downloadWorkbook(ss: SpreadsheetComponent, baseName: string, type: SheetFormat, token: string | undefined): Promise<void> {
    const blob = await renderWorkbook(ss, baseName, type, token);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}.${type}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function inferSheetType(name: string): SheetFormat {
    const m = name.match(/\.([^.]+)$/);
    const ext = (m?.[1] ?? "xlsx").toLowerCase();
    if (ext === "xls" || ext === "csv" || ext === "pdf") return ext;
    return "xlsx";
}

/**
 * The format Save must write to version `name` in place, or null when a
 * version would change the file's format (.json snapshots, anything else).
 * Note .csv versions hold the active sheet's values only — as in Excel.
 */
function versionFormat(name: string): Exclude<SheetFormat, "pdf"> | null {
    const ext = name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
    return ext === "xlsx" || ext === "xls" || ext === "csv" ? ext : null;
}

function toTracked(item: DriveItem): TrackedFile {
    return { id: item.id, name: item.name, parentId: item.parentId, role: item.role ?? null };
}

function unsupportedMessage(name: string): string {
    return `${name} can't be opened in Athavita Sheets. Supported file types are .xlsx, .xls, .csv and .json.`;
}

function errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

// Keeps the file's extension when the user types a bare name ("Budget" →
// "Budget.xlsx"). A different spreadsheet extension is swapped for the
// current one — a rename must never change what format the bytes are in.
function keepExtension(typed: string, current: string): string {
    const trimmed = typed.trim();
    const ext = current.match(/\.([^./\\]+)$/)?.[1];
    if (!ext) return trimmed;
    if (trimmed.toLowerCase().endsWith(`.${ext.toLowerCase()}`) && trimmed.length > ext.length + 1) return trimmed;
    const base = trimmed.replace(KNOWN_EXT_RE, "").trim();
    if (!base) throw new Error("Enter a file name.");
    return `${base}.${ext}`;
}

function SaveAsDialog({
    defaultName, defaultType, onClose, onConfirm
}: {
    defaultName: string;
    defaultType: SheetFormat;
    onClose: () => void;
    onConfirm: (target: "drive" | "computer", name: string, type: SheetFormat) => void;
}) {
    const [target, setTarget] = useState<"drive" | "computer">("drive");
    const [name, setName] = useState(defaultName);
    const [type, setType] = useState<SheetFormat>(defaultType);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                className="w-[460px] max-w-[92vw] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">Save As</h2>
                    <button type="button" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">✕</button>
                </div>
                <form
                    id="sheet-save-as-form"
                    className="flex flex-col gap-3 p-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (name.trim()) onConfirm(target, name.trim(), type);
                    }}
                >
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Save to</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setTarget("drive")}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${target === "drive" ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}
                            >
                                Drive
                            </button>
                            <button
                                type="button"
                                onClick={() => setTarget("computer")}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${target === "computer" ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}
                            >
                                Computer
                            </button>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="save-as-name" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Filename</label>
                        <input
                            id="save-as-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        />
                    </div>
                    <div>
                        <label htmlFor="save-as-type" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">File type</label>
                        <select
                            id="save-as-type"
                            value={type}
                            onChange={(e) => setType(e.target.value as SheetFormat)}
                            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            <option value="xlsx">Excel Workbook (.xlsx)</option>
                            <option value="xls">Excel 97-2003 Workbook (.xls)</option>
                            <option value="csv">CSV (.csv)</option>
                            <option value="pdf">PDF (.pdf)</option>
                        </select>
                    </div>
                </form>
                <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">
                    <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm hover:bg-accent">Cancel</button>
                    <button
                        type="submit"
                        form="sheet-save-as-form"
                        disabled={!name.trim()}
                        className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

function stripExtension(name: string): string {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return "Untitled";
    return trimmed.replace(/\.[^./\\]+$/, "");
}
